import { aliasesNamedBy } from '@agavi/blocks';

import type { Block, TaskVariable } from '@/types/experience';
import type { AnswerRefAlias } from '@agavi/blocks';

import { variablesUsedByBlock } from '@/components/task/variable-utils';


/**
 * What was last copied, across navigations.
 *
 * Copying a block into another task means leaving the page the block is on, so the clipboard has to
 * outlive the editor that filled it — hence `localStorage` rather than React state or a context.
 *
 * A SNAPSHOT, never a reference. What gets pasted is what was on screen at copy time: editing or
 * deleting the source afterwards leaves the clipboard untouched, which is the behaviour anyone who
 * has used a clipboard already expects, and it keeps paste working when the source experience is
 * gone entirely.
 *
 * `items` is an array even though a copy currently writes exactly one. Multi-select is a later
 * phase, and an array from the start means it arrives without a format change or a migration for
 * clipboards already sitting in people's browsers.
 */

export const CLIPBOARD_KEY = 'agavi.clipboard';
const CLIPBOARD_VERSION = 1;

export interface ClipboardBlockItem {
  block: Block;
  /** Only the variables this block actually references — resolved at copy time, so the paste knows
   * what to reconcile without needing the source task. */
  variables: TaskVariable[];
}

/** A whole task, as the paste endpoint wants it. Self-contained: a task carries its own variables,
 *  so unlike a block it needs nothing reconciled against where it lands. */
export interface ClipboardTask {
  sourceId: string;
  title?: string;
  authorId?: string | null;
  points?: number;
  penalty?: number;
  scoringMode?: string;
  blocks: Block[];
  variables?: TaskVariable[];
  flow?: unknown;
  /**
   * The pathway this task belonged to where it was copied from — carried so a copied pathway can
   * keep its name when it is pasted as a branch somewhere else. `pathwayId` deliberately does NOT
   * travel: the paste mints a fresh one, because a pathway is identified by the task it serves.
   */
  pathwayName?: string;
  /**
   * The member this task hangs off WITHIN the copied set, or absent for a root.
   *
   * Already resolved at copy time by `resolveSelectionParents` — it is not the source board's
   * `pathwayOf`. A sparse selection can skip a layer, and only the board can climb past the gap, so
   * the clipboard stores the answer and the paste has nothing left to work out.
   */
  pathwayOf?: string | null;
  /**
   * The pathway this task belonged to on the source board, used ONLY to group members into lanes
   * on arrival. Not reused as the new id — the paste always mints a fresh one, because a pathway is
   * identified by the task it serves. Without it, two different pathways hanging off the SAME host
   * are indistinguishable once lifted out, and they merge into one lane.
   */
  sourcePathwayId?: string | null;
  /**
   * The EXPERIENCE-scoped variables this task's blocks actually resolve against.
   *
   * An experience variable is defined once and resolves in every task, so it is not in
   * `variables` and a task carried away from its experience arrives with nothing behind
   * `{river}`: every equation reading it reports a non-numeric answer, and nothing says why.
   * The BLOCK clipboard already captures both scopes for exactly this reason; the task
   * clipboard was the half that still did not.
   *
   * Kept SEPARATE from `variables` rather than folded in at copy time, because a paste back
   * into this same experience must not fold them at all — the names already resolve there, and
   * promoting them to task scope would turn one per-session value into a per-task one. See
   * `foldExperienceVariables`, which is what the cross-document paste calls.
   */
  experienceVariables?: TaskVariable[];
}

/**
 * A copied task, made self-sufficient for a paste into a DIFFERENT experience.
 *
 * The experience variables it carried become its own. They keep their ids, which is what keeps
 * them honest: `deriveSeed` hashes the id, so two tasks pasted together that shared `{river}`
 * go on sharing one value instead of drifting apart the moment they land.
 *
 * An experience variable WINS a name collision, because it already won one at the source — a
 * task variable of the same name was shadowed there, unreachable by anything in the task, and
 * reported as a defect by `shadowedTaskVariables`. Carrying the dead one and dropping the live
 * one would be the copy changing what the task does.
 */
export function foldExperienceVariables(task: ClipboardTask): ClipboardTask {
  const { experienceVariables, ...rest } = task;
  if (!experienceVariables?.length) return rest;
  const shadowed = new Set(experienceVariables.map(v => v.name));
  return {
    ...rest,
    variables: [...experienceVariables, ...(task.variables ?? []).filter(v => !shadowed.has(v.name))],
  };
}

interface ClipboardBase {
  v: number;
  copiedAt: string;
  source: { experienceId: string; taskId?: string };
}

/**
 * A task as the clipboard carries it, deep-cloned so nothing downstream can reach back into
 * the live document.
 *
 * Shared because pull-through snapshots tasks from a DIFFERENT experience than the one being
 * edited, from a second surface, and two hand-rolled versions of "what a copied task is" is
 * exactly the drift that costs a field the day one of them grows one.
 */
export function snapshotTask(task: {
  id: string;
  title?: string;
  authorId?: string | null;
  points?: number;
  penalty?: number;
  scoringMode?: string;
  blocks?: unknown;
  variables?: unknown;
  flow?: unknown;
  pathwayName?: string | null;
  pathwayId?: string | null;
},
/** The whole experience's variables. Narrowed here to the ones this task's blocks reach. */
experienceVariables?: TaskVariable[],
): ClipboardTask {
  const clone = <T,>(v: T): T => (v === undefined ? v : JSON.parse(JSON.stringify(v)) as T);
  const blocks = clone(task.blocks ?? []) as ClipboardTask['blocks'];
  /*
   * TRANSITIVELY, the same walk the block clipboard makes: a task reading `{luminosity}` needs
   * the `{mass}` that equation is built from, or it arrives with an input it cannot compute.
   */
  const usedExperienceVars = experienceVariables?.length
    ? (() => {
      const wanted = new Map<string, TaskVariable>();
      for (const b of blocks) {
        for (const v of variablesUsedByBlock(b, experienceVariables)) wanted.set(v.id, v);
      }
      return wanted.size > 0 ? clone([...wanted.values()]) : undefined;
    })()
    : undefined;
  return {
    sourceId: task.id,
    title: task.title,
    authorId: task.authorId,
    points: task.points,
    penalty: task.penalty,
    scoringMode: task.scoringMode,
    blocks,
    variables: clone(task.variables) as ClipboardTask['variables'],
    experienceVariables: usedExperienceVars,
    flow: clone(task.flow),
    pathwayName: task.pathwayName ?? undefined,
    sourcePathwayId: task.pathwayId ?? undefined,
  };
}

/**
 * IS THIS PASTE LANDING IN THE DOCUMENT ITS TASKS CAME FROM?
 *
 * Two things hang on the answer: whether the content has to be sent at all (the server can
 * hydrate a task it already holds, which is what keeps a multi-task paste inside the body
 * limit), and whether the experience variables a task carried have to become its own.
 *
 * Asked of the TASKS, not of the clipboard, which was the subtle part. The clipboard answered
 * it for every paste including the ones that never touch it: Duplicate builds its set straight
 * off the board, so with an empty clipboard — or one filled from another experience three days
 * ago — an in-place duplicate was classed as foreign. Harmless while the only cost was sending
 * content needlessly, and not harmless at all once a misread also promotes experience-scoped
 * variables into task-scoped copies of themselves.
 *
 * A task is from here when the id it was copied from is a task that is still here, which is
 * exactly the test the paste endpoint itself applies when it decides whether to hydrate.
 */
export function isSameDocumentPaste(tasks: ClipboardTask[], liveTaskIds: Set<string>): boolean {
  return tasks.length > 0 && tasks.every(t => liveTaskIds.has(t.sourceId));
}

/**
 * THE ALIASES A PASTE HAS TO SEND: the copied set's, plus the PULLED tasks' own.
 *
 * Pull-through chooses tasks transitively — the task holding an answer can itself cite an
 * answer in a task pulled behind it — while the alias payload was only ever the census of what
 * the teacher copied. The deepest pulled task therefore landed holding a chip naming an alias
 * nobody sent. That is worse than a dangling address: an address can be read and repointed,
 * and a bare id that resolves to nothing says nothing at all about what it meant.
 *
 * Deduplicated by id, with the clipboard's copy winning: it is the one the arriving chips
 * were written against, and the server's install rules are stated in terms of it.
 */
export function withPulledAliases(
  carried: AnswerRefAlias[],
  pulled: ClipboardTask[],
  sourceAliases: AnswerRefAlias[],
): AnswerRefAlias[] {
  if (sourceAliases.length === 0) return carried;
  const byId = new Map(carried.map(a => [a.id, a]));
  for (const a of aliasesNamedBy(pulled.flatMap(t => t.blocks) as never, sourceAliases)) {
    if (!byId.has(a.id)) byId.set(a.id, a);
  }
  return [...byId.values()];
}

/**
 * A DISCRIMINATED union, so nothing can paste a task where a block goes.
 *
 * The two live in one clipboard because there is one Copy gesture as far as a teacher is concerned,
 * and because holding both at once would raise a question nobody asked. Narrowing on `kind` is what
 * makes each paste surface refuse the other's payload at compile time rather than at runtime.
 */
export interface ClipboardBlocks extends ClipboardBase {
  kind: 'blocks';
  items: ClipboardBlockItem[];
  /**
   * The block Copy was actually pressed on. `items` is in the SOURCE TASK's order so a pasted set
   * arrives looking like itself, which means the subject is not reliably first. Absent on a
   * clipboard written before this existed — fall back to `items[0]`, which is what a single-item
   * copy has always been.
   */
  subjectId?: string;
  /**
   * The aliases these blocks' chips name, for the reason `ClipboardTasks.aliases` gives.
   *
   * Blocks were the half that was missed. Unlike a task paste, the SOURCE TASK does not travel,
   * so an alias arriving in another experience resolves to nothing — but it arrives carrying
   * its members' `label` epitaphs, so the reference is a named row the manager can repoint
   * rather than a dead id that says nothing about what it used to mean.
   *
   * Absent on a clipboard written before this existed, and on any copy holding no references.
   */
  aliases?: AnswerRefAlias[];
}
export interface ClipboardTasks extends ClipboardBase {
  kind: 'tasks';
  tasks: ClipboardTask[];
  /**
   * The answer-reference aliases these tasks' chips name.
   *
   * A chip stores an alias id and nothing else, so a task pasted away from its aliases arrives
   * with every reference broken — and a dangling alias id, unlike a dangling address, says
   * nothing about what it meant. They ride on the clipboard for the same reason the content
   * does: it is a SNAPSHOT, so a teacher can copy, tidy the original away, and still paste what
   * they took.
   *
   * Absent on a clipboard written before this existed, and on any copy of tasks that hold no
   * references.
   */
  aliases?: AnswerRefAlias[];
}
export type ClipboardPayload = ClipboardBlocks | ClipboardTasks;

/**
 * Anything at all can be in `localStorage` — a payload from a newer build, a half-written value, a
 * key a person set by hand — and none of it should be able to take the editor down. Everything
 * unrecognised reads as "nothing copied", which is a state the UI already handles.
 */
function isPayload(value: unknown): value is ClipboardPayload {
  if (!value || typeof value !== 'object') return false;
  // Deliberately loose: an INTERSECTION of the two members makes `kind` the impossible
  // `'blocks' & 'tasks'`, and narrowing on it then yields `never`. This validator's whole job is to
  // inspect something it cannot yet claim a type for.
  const p = value as { v?: unknown; kind?: unknown; source?: unknown; items?: unknown; tasks?: unknown };
  if (p.v !== CLIPBOARD_VERSION || !p.source || typeof p.source !== 'object') return false;

  if (p.kind === 'blocks') {
    if (!Array.isArray(p.items) || p.items.length === 0) return false;
    return (p.items as ClipboardBlockItem[]).every(item =>
      !!item
      && !!item.block
      && typeof item.block.id === 'string'
      && typeof item.block.type === 'string'
      && Array.isArray(item.variables),
    );
  }

  if (p.kind === 'tasks') {
    if (!Array.isArray(p.tasks) || p.tasks.length === 0) return false;
    return (p.tasks as ClipboardTask[]).every(task =>
      !!task && typeof task.sourceId === 'string' && Array.isArray(task.blocks),
    );
  }

  return false;
}

export function readClipboard(): ClipboardPayload | null {
  try {
    const raw = window.localStorage.getItem(CLIPBOARD_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isPayload(parsed) ? parsed : null;
  } catch {
    // A private window, blocked site data, or a value that is not JSON. Not an error worth
    // surfacing — it only ever means there is nothing to paste.
    return null;
  }
}

/** Distributive, so each member of the union keeps its OWN shape. A plain `Omit` over a union
 *  collapses to the keys they share, which would let a caller write a task payload with no tasks. */
type Unstamped<T> = T extends unknown ? Omit<T, 'v' | 'copiedAt'> : never;

export function writeClipboard(payload: Unstamped<ClipboardPayload>): void {
  try {
    const full = { ...payload, v: CLIPBOARD_VERSION, copiedAt: new Date().toISOString() } as ClipboardPayload;
    window.localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(full));
  } catch {
    // Quota exceeded or storage blocked. The copy silently does not persist; the alternative is an
    // error dialog for something the teacher can simply do again.
  }
}

export function clearClipboard(): void {
  try {
    window.localStorage.removeItem(CLIPBOARD_KEY);
  } catch { /* see readClipboard */ }
}

/**
 * Fires when another tab copies something, so a paste affordance opened in one tab does not go on
 * offering a block that a second tab has since replaced. `storage` only fires in the OTHER tabs, so
 * the copying tab updates its own state directly.
 */
export function subscribeClipboard(onChange: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === null || e.key === CLIPBOARD_KEY) onChange();
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
